// Admin · Users tab — paginated list with filters, search, sort and a detail
// modal fed by GET /api/admin/users/{id}.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X as XIcon, Search as SearchIcon } from "lucide-react";

import { API } from "../../../lib/api";

const FILTERS = [
  { key: "all", label: "TODOS" },
  { key: "pro", label: "PRO" },
  { key: "free", label: "FREE" },
];
const SORTS = [
  { key: "newest", label: "Más recientes" },
  { key: "oldest", label: "Más antiguos" },
  { key: "pro_date", label: "Fecha PRO" },
];

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); }
  catch { return "—"; }
};

export default function UsersTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);

  // Debounce the search input so we don't spam Mongo on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ filter, sort, page: String(page), limit: "50" });
    if (search) qs.set("search", search);
    if (includeTest) qs.set("include_test_domains", "true");
    fetch(`${API}/admin/users?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.clone().json())
      .then((d) => { if (alive) { setData(d); setErr(null); } })
      .catch((e) => { if (alive) setErr(String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, filter, sort, page, search, includeTest]);

  const totalPages = data?.total_pages || 1;

  return (
    <div data-testid="admin-tab-users" className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => { setFilter(f.key); setPage(1); }}
            data-testid={`admin-users-filter-${f.key}`}
            className="rounded-md px-3 py-1 font-mono text-[10px] font-bold transition-all"
            style={filter === f.key
              ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
              : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto flex-1 max-w-[260px]">
          <SearchIcon className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-white/35" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="admin-users-search"
            placeholder="Buscar email…"
            className="w-full rounded-md border border-white/10 bg-white/[0.02] py-1 pl-7 pr-2 font-mono text-[11px] text-white placeholder-white/30 focus:border-[#f97316]/60 focus:outline-none"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="admin-users-sort"
          className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1 font-mono text-[10px] text-white/70"
        >
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button
          type="button"
          onClick={() => { setIncludeTest((v) => !v); setPage(1); }}
          data-testid="admin-users-toggle-test"
          aria-pressed={includeTest}
          title="Mostrar/ocultar dominios @rebel.dev y @rebel.test"
          className="rounded-md px-2.5 py-1 font-mono text-[10px] font-bold transition-all"
          style={includeTest
            ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
            : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }}
        >
          {includeTest ? "🧪 Mostrando test" : "🚫 Ocultar test"}
        </button>
      </div>

      {/* Counts row */}
      {data && (
        <p className="font-mono text-[9px] uppercase tracking-wider text-white/45">
          {data.total_usuarios} usuarios · {data.total_pro} PRO · {data.total_gratuitos} free · página {data.page}/{data.total_pages}
        </p>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-white/10">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 font-mono text-[11px] text-white/55">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        )}
        {err && <p className="px-4 py-6 font-mono text-[11px] text-[#ff3b5c]">Error: {err}</p>}
        {!loading && data && (
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-left text-[9px] uppercase tracking-wider text-white/45">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Creado</th>
                <th className="px-3 py-2">PRO desde</th>
                <th className="px-3 py-2 text-right">XP</th>
              </tr>
            </thead>
            <tbody>
              {(data.usuarios || []).map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setDetailId(u.id)}
                  data-testid={`admin-users-row-${u.id}`}
                  className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                >
                  <td className="px-3 py-2 text-white/85">{u.email}</td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block rounded px-1.5 py-[1px] text-[8px] font-bold"
                      style={u.is_pro
                        ? { background: "rgba(0,208,132,0.15)", color: "#00d084" }
                        : { background: "rgba(255,255,255,0.05)", color: "#aaa" }}
                    >
                      {u.is_pro ? "PRO" : "FREE"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-white/60">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2 text-white/60">{fmtDate(u.pro_since)}</td>
                  <td className="px-3 py-2 text-right text-white/75">{u.xp ?? 0}</td>
                </tr>
              ))}
              {(data.usuarios || []).length === 0 && (
                <tr><td colSpan="5" className="px-3 py-8 text-center text-white/45">Sin resultados.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2 pt-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          data-testid="admin-users-prev"
          className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[10px] text-white/70 disabled:opacity-40"
        >
          ← Anterior
        </button>
        <span className="font-mono text-[10px] text-white/55">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          data-testid="admin-users-next"
          className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[10px] text-white/70 disabled:opacity-40"
        >
          Siguiente →
        </button>
      </div>

      {detailId && (
        <UserDetailModal id={detailId} token={token} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

function UserDetailModal({ id, token, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/admin/users/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.clone().json())
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [id, token]);

  const rows = useMemo(() => {
    if (!data) return [];
    return [
      ["Email", data.email],
      ["ID", data.id],
      ["Estado", data.is_pro ? "PRO" : "FREE"],
      ["Admin", data.is_admin ? "SÍ" : "no"],
      ["Creado", fmtDate(data.created_at)],
      ["PRO desde", fmtDate(data.pro_since)],
      ["Método PRO", data.pro_method || "—"],
      ["Stripe session", data.stripe_session_id || "—"],
      ["RBL tx hash", data.rbl_tx_hash || "—"],
      ["Login count", data.login_count],
      ["IPs recientes", (data.ip_addresses_recientes || []).join(", ") || "—"],
      ["XP", data.xp],
      ["Nivel academy", data.academy_progress?.nivel_actual ?? 0],
      ["Lecciones completadas", data.academy_progress?.lecciones_completadas ?? 0],
      ["Shadow Network activo", data.shadow_network_activo ? "SÍ" : "no"],
      ["Diplomas NFT", (data.diplomas_nft || []).length],
    ];
  }, [data]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="admin-user-detail-modal"
    >
      <div
        className="relative w-full max-w-[640px] overflow-hidden rounded-xl border"
        style={{ background: "rgba(8,5,8,0.97)", borderColor: "rgba(249,115,22,0.35)", maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#f97316]/20 px-4 py-3">
          <p className="font-mono text-[11px] font-bold tracking-[2px] text-[#f97316]">
            👤 DETALLE DE USUARIO
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-user-detail-close"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-white/60 hover:text-white"
          >
            <XIcon size={14} />
          </button>
        </div>
        <div className="overflow-y-auto p-4" style={{ maxHeight: "calc(85vh - 60px)" }}>
          {!data && !err && (
            <div className="flex items-center gap-2 font-mono text-[11px] text-white/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          )}
          {err && <p className="font-mono text-[11px] text-[#ff3b5c]">Error: {err}</p>}
          {data && (
            <dl className="grid grid-cols-1 gap-y-1.5 font-mono">
              {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-white/5 py-1.5">
                  <dt className="text-[10px] uppercase tracking-wider text-white/45">{k}</dt>
                  <dd className="break-all text-right text-[11px] text-white/85">{String(v ?? "—")}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
